import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import DefaultModal from '../../../components/default/DefaultModal';
import { useHistory } from 'react-router-dom';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import DefaultToolBar from '../../../components/default/DefaultToolBar';
import useBusinessNameBankAccountService from '../../../app/services/hooks/useBusinessNameBankAccountService';
import { BankAccountClientDataForm, BankAccountClientFilters } from '../../../app/models/BankAccountClient';
import BusinessNameBankAccountDataTable from '../../BusinessName/components/tables/BusinessNameBankAccountDataTable';
import { BusinessNameBankAccountDataForm } from '../../../app/models/BusinessNameBankAccount';
import BusinessNameBankAccountCreate from '../../BusinessName/components/BusinessNameBankAccountCreate';
import BusinessNameBankAccountEdit from '../../BusinessName/components/BusinessNameBankAccountEdit';

interface Props {
    societyId: number;
    businessNameId: number;
    isShow?: boolean;
}

const SocietyBankAccountContainer = ({ societyId, businessNameId, isShow = false }: Props) => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const {
        getAllBusinessNameBankAccountsBySociety,
        fetchingGetAllBusinessNameBankAccountsBySociety,
        deleteBusinessNameBankAccount,
        fetchingDeleteBusinessNameBankAccount
    } = useBusinessNameBankAccountService();

    const [showingCreate, setShowingCreate] = useState(false);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingShow, setShowingShow] = useState(false);
  
    const [bankAccountbusinessNameIdEdit, setBankAccountbusinessNameIdEdit] = useState<number>(-1);
    const [bankAccountbusinessNameIdShow, setBankAccountbusinessNameIdShow] = useState<number>(-1);
    
    const [businessNamebankAccounts, setBusinessNamebankAccounts] = useState<BusinessNameBankAccountDataForm[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [filter, setFilter] = useState<BankAccountClientFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'asc'
    });

    useEffect(() => {
        if (businessNameId > 0) {
            reloadTable();
        }
    }, [filter, businessNameId]);

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (bankAccountbusinessNameId: number) => {
        setShowingEdit(true);
        setBankAccountbusinessNameIdEdit(bankAccountbusinessNameId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setBankAccountbusinessNameIdEdit(-1);
    };

    const showShow = (bankAccountbusinessNameId: number) => {
        setShowingShow(true);
        setBankAccountbusinessNameIdShow(bankAccountbusinessNameId);
    };

    const hideShow = () => {
        setShowingShow(false);
        setBankAccountbusinessNameIdShow(-1);
    };

    const destroy = (bankAccountbusinessNameId: number) => {
        const _text = 'Está a punto de eliminar la cuenta bancaria #' + bankAccountbusinessNameId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading)
                            showLoading('loading', 'Eliminando cuenta bancaria...');
                        deleteBusinessNameBankAccount(bankAccountbusinessNameId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'la cuenta bancaria no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const reloadTable = () => {
        if (businessNameId > 0) {
            getAllBusinessNameBankAccountsBySociety(filter, societyId, {
                onSuccess: (response: ServiceResponse) => {
                    setBusinessNamebankAccounts(response.data.bank_accounts);
                    setTotalRows(response.data.total_rows);
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                }
            });
        }
    };

    if (businessNameId === 0) {
        return null;
    }

    return (
        <>
            <DefaultCard>
                <div className="h3 mt-0 mb-4 card-title">Cuentas bancarias</div>
                <div className="row">
                    <div className="col-12">
                        {!isShow ? (
                            <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} />} />
                        ) : null}
                    </div>
                    <div className="col-12">
                        {isShow ? (
                            <BusinessNameBankAccountDataTable
                                businessNamebankAccounts={businessNamebankAccounts}
                                loading={fetchingGetAllBusinessNameBankAccountsBySociety}
                                totalRows={totalRows}
                                filter={filter}
                                setFilter={setFilter}
                                callbackSuccess={reloadTable}
                                notActions={false}
                            />
                        ) : (
                            <BusinessNameBankAccountDataTable
                                businessNamebankAccounts={businessNamebankAccounts}
                                edit={showEdit}
                                destroy={destroy}
                                loading={fetchingGetAllBusinessNameBankAccountsBySociety}
                                totalRows={totalRows}
                                filter={filter}
                                setFilter={setFilter}
                                callbackSuccess={reloadTable}
                                notActions={false}
                            />
                        )}
                    </div>
                </div>
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear cuenta bancaria"
                    backdrop={true}
                    showFooter={false}
                >
                    <BusinessNameBankAccountCreate
                        businessNameId={businessNameId}
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}
            
            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title='Editar cuenta bancaria'
                    backdrop={true}
                    showFooter={false}
                >
                    <BusinessNameBankAccountEdit
                        businessNamebankAccountId={bankAccountbusinessNameIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null} 
        </>
    );
};

export default SocietyBankAccountContainer;
